import useRoutes from '../../hooks/useRoutes';
import { ComponentSectionContextType } from '../../types/context';
import { Dataset, DatasetControl, FormControlAction } from '../../types/types';
import { getSubsetAndItemParams } from '../../utils/getSubsetAndItemParams';
import { getDataKey } from '../getDataKey';
import { debugLogger } from '../../debug/DebugLogger';

const log = debugLogger.scope('formActions');

const logNotImplementedFallback = (
  componentSection: ComponentSectionContextType['componentSection'],
  control: DatasetControl,
  dataset: Dataset,
  rowToUse: unknown[] | undefined,
  extras?: Record<string, unknown>
) => {
  const controlAction = control.action as FormControlAction;

  /** @debugFeature formActions Logs a form action that fell back to the not-implemented route: section, form type and id, dataset, selected row count and the ids the failing branch needed. */
  log('form action url fell back to not-implemented route', () => ({
    componentSection,
    formType: controlAction.formType,
    formId: controlAction.formId,
    datasetId: dataset.datasetId,
    rowCount: rowToUse?.length,
    ...extras,
  }));
};

export const getFormActionUrlFromComponentSection = (
  componentSection: ComponentSectionContextType['componentSection'],
  routes: ReturnType<typeof useRoutes>,
  control: DatasetControl,
  dataset: Dataset,
  rowToUse: any,
  slaveTabIndex: number,
  pathname: string,
  masterExpandedRow: any,
  referenceDatasetId?: string,
  paramItemId?: string
) => {
  const retypedControlAction = control.action as FormControlAction;
  const redirectToAddForm = retypedControlAction.formType === 'insert';
  const redirectToEditForm = retypedControlAction.formType === 'edit';
  const redirectToCopyForm = retypedControlAction.formType === 'copy';
  const datasetKey = getDataKey(dataset);

  switch (componentSection) {
    case 'list':
      if (redirectToAddForm) {
        return routes.formListAddAction(
          dataset.datasetId,
          retypedControlAction.formId
        );
      }

      if (redirectToEditForm && rowToUse.length > 0) {
        return routes.formListEditAction(
          dataset.datasetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      if (redirectToCopyForm && rowToUse.length > 0) {
        return routes.formListCopyAction(
          dataset.datasetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      logNotImplementedFallback(componentSection, control, dataset, rowToUse);
      return routes.notImplemented();
    case 'detail':
      if (redirectToAddForm) {
        return routes.formListAddAction(
          dataset.datasetId,
          retypedControlAction.formId
        );
      }

      if (redirectToEditForm && rowToUse.length > 0) {
        return routes.formListEditAction(
          dataset.datasetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      if (redirectToCopyForm && rowToUse.length > 0) {
        return routes.formListCopyAction(
          dataset.datasetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      logNotImplementedFallback(componentSection, control, dataset, rowToUse);
      return routes.notImplemented();
    case 'list-subset':
      if (redirectToAddForm) {
        const subsetId = dataset.subsets[slaveTabIndex - 1].datasetId;
        return routes.formSubsetAddAction(
          dataset.datasetId,
          masterExpandedRow?.[datasetKey],
          subsetId,
          retypedControlAction.formId
        );
      }

      const { itemId: listSubsetItemId } = getSubsetAndItemParams(pathname);

      if (redirectToEditForm && listSubsetItemId && rowToUse.length > 0) {
        const subsetId = dataset.subsets[slaveTabIndex - 1].datasetId;
        return routes.formSubsetEditAction(
          dataset.datasetId,
          listSubsetItemId,
          subsetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      if (redirectToCopyForm && listSubsetItemId && rowToUse.length > 0) {
        const subsetId = dataset.subsets[slaveTabIndex - 1].datasetId;
        return routes.formSubsetCopyAction(
          dataset.datasetId,
          listSubsetItemId,
          subsetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      logNotImplementedFallback(componentSection, control, dataset, rowToUse, {
        listSubsetItemId,
      });
      return routes.notImplemented();
    case 'detail-subset':
      if (redirectToAddForm && paramItemId) {
        const subsetId = dataset.subsets[slaveTabIndex - 1].datasetId;
        return routes.formSubsetAddAction(
          dataset.datasetId,
          paramItemId,
          subsetId,
          retypedControlAction.formId
        );
      }

      if (redirectToEditForm && paramItemId && rowToUse.length > 0) {
        const subsetId = dataset.subsets[slaveTabIndex - 1].datasetId;
        return routes.formSubsetEditAction(
          dataset.datasetId,
          paramItemId,
          subsetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      if (redirectToCopyForm && paramItemId && rowToUse.length > 0) {
        const subsetId = dataset.subsets[slaveTabIndex - 1].datasetId;
        return routes.formSubsetCopyAction(
          dataset.datasetId,
          paramItemId,
          subsetId,
          retypedControlAction.formId,
          rowToUse?.[0]?.[datasetKey]
        );
      }

      logNotImplementedFallback(componentSection, control, dataset, rowToUse, {
        paramItemId,
      });
      return routes.notImplemented();
    case 'form-reference':
      if (referenceDatasetId) {
        return routes.formListAddAction(
          referenceDatasetId,
          retypedControlAction.formId
        );
      }

      logNotImplementedFallback(componentSection, control, dataset, rowToUse, {
        referenceDatasetId,
      });
      return routes.notImplemented();
    default:
      throw new Error(
        `Form action url for component section ${componentSection} is not implemented`
      );
  }
};
